import React from "react";
import { Copy, CheckCircle } from "lucide-react";

const CodeExamples = ({
  activeCodeTab = "python",
  setActiveCodeTab,
  copyToClipboard,
  copyStatus = {},
}) => {

  const codeExamples = {
    python: `# Python Translation API Example
import requests
import json

# API Configuration
API_URL = "https://developers-api.nkenne.ai/api/translate/with-api-key"
API_KEY = "your_api_key_here"

# Request payload
payload = {
    "text": "i am working on my app",
    "fromLanguage": "English",
    "toLanguage": "Yoruba"
}

# Headers
headers = {
    "Authorization": API_KEY,
    "Content-Type": "application/json"
}

# Make the request
try:
    response = requests.post(API_URL, headers=headers, json=payload)
    response.raise_for_status()
    
    result = response.json()
    print(f"Translated text: {result.get('translatedText', '')}")
    
except requests.exceptions.RequestException as e:
    print(f"Error: {e}")`,
    javascript: `// JavaScript Translation API Example
const API_URL = "https://developers-api.nkenne.ai/api/translate/with-api-key";
const API_KEY = "your_api_key_here";

// Request payload
const payload = {
    text: "i am working on my app",
    fromLanguage: "English",
    toLanguage: "Yoruba"
};

// Headers
const headers = {
    "Authorization": API_KEY,
    "Content-Type": "application/json"
};

// Make the request
async function translateText() {
    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: headers,
            body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
            throw new Error(\`HTTP error! status: \${response.status}\`);
        }
        
        const result = await response.json();
        console.log("Translated text:", result.translatedText);
        
    } catch (error) {
        console.error("Error:", error);
    }
}

// Call the function
translateText();`,
    dotnet: `// C# .NET Translation API Example
using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

public class TranslationService
{
    private readonly HttpClient _httpClient;
    private readonly string _apiUrl = "https://developers-api.nkenne.ai/api/translate/with-api-key";
    private readonly string _apiKey = "your_api_key_here";

    public TranslationService()
    {
        _httpClient = new HttpClient();
        _httpClient.DefaultRequestHeaders.Add("Authorization", _apiKey);
    }

    public async Task<string> TranslateTextAsync(string text, string fromLanguage, string toLanguage)
    {
        try
        {
            var payload = new
            {
                text = text,
                fromLanguage = fromLanguage,
                toLanguage = toLanguage
            };

            var json = JsonSerializer.Serialize(payload);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync(_apiUrl, content);
            response.EnsureSuccessStatusCode();

            var responseContent = await response.Content.ReadAsStringAsync();
            var result = JsonSerializer.Deserialize<TranslationResponse>(responseContent);

            return result.TranslatedText;
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Error: {ex.Message}");
            return string.Empty;
        }
    }
}

public class TranslationResponse
{
    public string TranslatedText { get; set; }
}

// Usage example
var translationService = new TranslationService();
var translatedText = await translationService.TranslateTextAsync(
    "i am working on my app", 
    "English", 
    "Yoruba"
);
Console.WriteLine($"Translated text: {translatedText}");`,
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Code Examples
        </h3>
        <p className="text-sm text-gray-600">
          Copy-paste these code snippets into your application to get started quickly.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-2 md:gap-4">
          {["python", "javascript", "dotnet"].map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveCodeTab(lang)}
              className={`py-2 px-4 border-b-2 font-medium text-sm capitalize transition-colors ${activeCodeTab === lang
                  ? "border-secondary text-secondary bg-emerald-50 rounded-t-lg"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              {lang === "dotnet" ? ".NET (C#)" : lang}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative">
        <pre className="text-green-400 text-sm font-mono leading-relaxed">
          <code>{codeExamples[activeCodeTab] || "// Code snippet unavailable"}</code>
        </pre>
      </div>

      <button
        onClick={() => copyToClipboard(codeExamples[activeCodeTab], "code")}
        className="flex items-center gap-2 text-secondary hover:text-green-700 font-medium text-sm transition-colors"
      >
        {copyStatus["code"] ? (
          <span className="flex items-center gap-1 text-green-500">
            <CheckCircle className="w-4 h-4" /> Copied Code!
          </span>
        ) : (
          <>
            <Copy className="w-4 h-4" /> Copy Code Snippet
          </>
        )}
      </button>
    </div>
  );
};

export default CodeExamples;
